import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listAllResource, listResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { credentialSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface EditForm {
  userId: string;
  templateId: string;
  status: string;
  expiresAt: string;
}

interface CredentialDetail {
  id: string;
  title: string;
  userId: string;
  templateId: string;
  status?: string;
  expiresAt?: string | null;
}

interface Option {
  id: string;
  name: string;
  fullName?: string;
  email?: string;
  title?: string;
}

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-3 px-4 text-base outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

function dateInputValue(value?: string | null) {
  if (!value) return '';
  return value.slice(0, 10);
}

export function CredentialEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const { register, handleSubmit, reset, watch, setValue } = useForm<EditForm>({
    defaultValues: { userId: '', templateId: '', status: 'issued', expiresAt: '' },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['credentials', id],
    queryFn: () => api<CredentialDetail>(`/credentials/${id}`),
    enabled: Boolean(id),
  });
  const { data: users } = useQuery({ queryKey: ['users'], queryFn: () => listAllResource<Option>('/users') });
  const selectedUserId = watch('userId');
  const filteredUsers = useMemo(() => {
    const list = users?.data ?? [];
    if (!search.trim()) return list;
    return list.filter(
      (u) => matchesSearch(u.fullName ?? u.name, search) || matchesSearch(u.email, search),
    );
  }, [users?.data, search]);
  const { data: templates } = useQuery({
    queryKey: ['credential-templates'],
    queryFn: () => listResource<Option>('/credentials/templates'),
  });

  useEffect(() => {
    if (!data) return;
    reset({
      userId: data.userId,
      templateId: data.templateId,
      status: data.status ?? 'issued',
      expiresAt: dateInputValue(data.expiresAt),
    });
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: (values: EditForm) =>
      api(`/credentials/${id}`, {
        method: 'PATCH',
        body: {
          userId: values.userId,
          templateId: values.templateId,
          status: values.status,
          expiresAt: values.expiresAt || null,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['credentials'] });
      toast(credentialSuccessToast('edit'));
      navigate(`/credenciales/${id}`);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo guardar la credencial'));
    },
  });

  return (
    <div className="lms-credentials-issue lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to={`/credenciales/${id}`} label="Volver a la credencial" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Editar credencial</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {data?.title ?? 'Actualiza el destinatario, la plantilla, el estado o la expiración.'}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit((values) => mutation.mutate(values))}
          className="lms-credentials-glass-panel w-full rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8"
        >
          <div className="space-y-4">
            <div className="space-y-3">
              <input type="hidden" {...register('userId', { required: true })} />
              <label htmlFor="user-search" className="text-sm font-medium text-dash-on-surface-variant">
                Estudiante <span className="text-error-600">*</span>
              </label>

              <div className="relative">
                <span
                  className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-dash-on-surface-variant"
                  aria-hidden="true"
                >
                  search
                </span>
                <input
                  id="user-search"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar estudiante por nombre o correo..."
                  className="h-10 w-full rounded-lg border border-dash-outline-variant bg-secondary-50 pl-12 pr-4 text-sm outline-none transition-all focus:border-dash-secondary focus:bg-white focus:ring-2 focus:ring-dash-secondary/20"
                />
              </div>

              <div className="overflow-hidden rounded-lg border border-dash-outline-variant bg-white">
                <div className="max-h-64 overflow-y-auto p-1">
                  <div className="space-y-1">
                    {filteredUsers.length === 0 ? (
                      <p className="px-3 py-6 text-center text-sm text-dash-on-surface-variant">
                        No hay estudiantes que coincidan con la búsqueda.
                      </p>
                    ) : (
                      filteredUsers.map((u) => {
                        const checked = selectedUserId === u.id;
                        return (
                          <label
                            key={u.id}
                            className={`flex cursor-pointer items-center gap-4 rounded-md p-3 transition-colors hover:bg-secondary-50 ${
                              checked ? 'bg-brand-50' : ''
                            }`}
                          >
                            <input
                              type="radio"
                              name="student_choice"
                              checked={checked}
                              onChange={() => setValue('userId', u.id, { shouldValidate: true })}
                              className="h-5 w-5 border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
                            />
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-dash-on-surface-variant">
                              <span className="material-symbols-outlined" aria-hidden="true">
                                person
                              </span>
                            </div>
                            <div className="min-w-0 flex-grow">
                              <p className="text-sm font-medium text-dash-on-surface">{u.fullName ?? u.name}</p>
                              {u.email && (
                                <p className="truncate text-xs text-dash-on-surface-variant">{u.email}</p>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="templateId" className="text-sm font-medium text-dash-on-surface-variant">
                Plantilla <span className="text-error-600">*</span>
              </label>
              <select id="templateId" required className={FIELD} {...register('templateId', { required: true })}>
                <option value="">Seleccionar plantilla</option>
                {(templates?.data ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name ?? template.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label htmlFor="status" className="text-sm font-medium text-dash-on-surface-variant">
                Estado <span className="text-error-600">*</span>
              </label>
              <select id="status" required className={FIELD} {...register('status', { required: true })}>
                <option value="issued">Emitida</option>
                <option value="revoked">Revocada</option>
                <option value="expired">Expirada</option>
              </select>
            </div>
            <div className="space-y-2">
              <label htmlFor="expiresAt" className="text-sm font-medium text-dash-on-surface-variant">
                Expira
              </label>
              <input id="expiresAt" type="date" className={FIELD} {...register('expiresAt')} />
            </div>
          </div>
          <button
            type="submit"
            disabled={mutation.isPending || !selectedUserId}
            className="mt-8 w-full rounded-lg bg-dash-primary px-6 py-3 text-sm font-medium text-white disabled:opacity-60"
          >
            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
          <Link to={`/credenciales/${id}`} className="mt-3 block text-center text-sm text-dash-secondary hover:underline">
            Cancelar
          </Link>
        </form>
      )}
    </div>
  );
}
